import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { bizuApi } from '../../api/bizu.api';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function QuesoLibraryPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLibrary = async () => {
      setLoading(true);
      try {
        const response = await bizuApi.getQuesoLibrary(userId);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching Queso library:', err);
        setError('Failed to load library');
      } finally {
        setLoading(false);
      }
    };

    fetchLibrary();
  }, [userId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="mb-6">
          <Button
            variant="secondary"
            onClick={() => navigate('/bizu/queso')}
            className="mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Queso
          </Button>
        </div>
        <div className="text-center py-8 text-gray-500">
          {error || 'Library not found'}
        </div>
      </DashboardLayout>
    );
  }

  const { user, products, totalProducts, completedProducts } = data;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => navigate('/bizu/queso')}
          className="mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Queso
        </Button>
      </div>

      {/* User Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.fullName}'s Library</h1>
            <p className="text-gray-500">@{user.username}</p>
            {user.role && (
              <Badge variant={user.role === 'adult' ? 'info' : 'secondary'} className="mt-2">
                {user.role}
              </Badge>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Cards Completed</p>
            <p className="text-3xl font-bold text-gray-900">
              {completedProducts} <span className="text-lg text-gray-400">/ {totalProducts}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Products/Cards List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Cards ({products.length})
          </h2>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No cards in library yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Look
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Snif
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Feel
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product, index) => (
                  <tr key={product.product_id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.product}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {product.completed ? (
                        <Badge variant="success">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Completed
                        </Badge>
                      ) : (
                        <Badge variant="warning">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Incomplete
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.look || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.snif || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.feel || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
